import { type ComponentTFunction } from '@pkgs/components/providers/AppContextProvider';
import {
  BusinessArea,
  COUNTRYCODE_PLACEID_MAP,
  MarketCountryCode,
  PropertyMarketingType,
  PropertyType,
  type FeaturesFilter,
  type Language,
  type PropertySubType,
  type RangeFilter,
  type SearchFilterRequestPropertyTypeSubType,
  UNIT_SYMBOL_MAP,
  type PlaceDetails,
  type Currency,
  getEntries,
} from '@ev-tech/search-modules-api';

import { getFeatureKey } from './translationKeyUtils';
import { formatNumber, formatPriceWithoutDecimals } from './numberFormatters';
import { formatDisplayRange, type DisplayRange } from './filterLabelUtils';
import { getPropertyTypeSelectionEntries } from './aiSearchPropertyTypeUtils';
import { getSelectedPreferenceEntries } from './aiSearchPreferenceUtils';
import { FILTER_CHIP_KINDS, PROPERTY_TYPE_SELECTION_KINDS } from './aiSearchKinds';
import { isNonEmptyObject } from './searchStateUtils';
import { type SearchModuleTranslationKey } from '../types/typedTranslationKeys';
import { type RangesState } from '../hooks/stores/search/slices/rangesSlice';
import { type UIFacingPreferences } from '../hooks/stores/search/slices/preferencesSlice';
import { DEFAULT_SORTING_OPTION_ID } from '../hooks/stores/search/slices/optionsSlice';
import {
  DEFAULT_BUSINESS_AREA,
  DEFAULT_PROPERTY_MARKETING_TYPE,
  initialPropertyTypeState,
  type ObjectTypeCategory,
  type PropertyTypeState,
} from '../hooks/stores/search/slices/objectTypeFilterSlice';
import { type SearchState } from '../hooks/stores/search/SearchStoreProvider';
import { type PromptOptions } from '../hooks/queries/useAiSearch';
import { SortingOptionId } from '../enums/enums';

type RangeField = keyof RangesState;

export type FilterChip =
  | {
      id: `${typeof FILTER_CHIP_KINDS.marketingType}:${PropertyMarketingType}`;
      isRemovable: boolean;
      label: string;
      kind: typeof FILTER_CHIP_KINDS.marketingType;
      value: PropertyMarketingType;
      variant: 'filter';
    }
  | {
      id: `${typeof FILTER_CHIP_KINDS.location}:${string}`;
      isRemovable: boolean;
      label: string;
      kind: typeof FILTER_CHIP_KINDS.location;
      variant: 'filter';
      value: string;
    }
  | {
      id: `${typeof FILTER_CHIP_KINDS.objectType}:${string}`;
      isRemovable: boolean;
      label: string;
      kind: typeof FILTER_CHIP_KINDS.objectType;
      value: SearchState['objectTypeCategory'];
      variant: 'filter';
    }
  | {
      id: `${typeof FILTER_CHIP_KINDS.propertyType}:${string}:${string}`;
      isRemovable: true;
      label: string;
      kind: typeof FILTER_CHIP_KINDS.propertyType;
      propertyType: keyof SearchFilterRequestPropertyTypeSubType;
      variant: 'filter';
    }
  | {
      id: `${typeof FILTER_CHIP_KINDS.propertySubType}:${string}:${string}:${string}`;
      isRemovable: true;
      label: string;
      kind: typeof FILTER_CHIP_KINDS.propertySubType;
      propertyType: keyof SearchFilterRequestPropertyTypeSubType;
      propertySubType: PropertySubType;
      variant: 'filter';
    }
  | {
      id: `${typeof FILTER_CHIP_KINDS.range}:${keyof SearchState}`;
      isRemovable: true;
      label: string;
      kind: typeof FILTER_CHIP_KINDS.range;
      field: RangeField;
      variant: 'filter';
    }
  | {
      id: `${typeof FILTER_CHIP_KINDS.feature}:${FeaturesFilter}`;
      isRemovable: true;
      label: string;
      kind: typeof FILTER_CHIP_KINDS.feature;
      feature: FeaturesFilter;
      variant: 'filter';
    }
  | {
      id: `${typeof FILTER_CHIP_KINDS.preference}:${keyof UIFacingPreferences}`;
      isRemovable: true;
      label: string;
      kind: typeof FILTER_CHIP_KINDS.preference;
      preference: keyof UIFacingPreferences;
      variant: 'preference';
    };

type ConvertRangeToTextParams = {
  range: DisplayRange;
  translationKey?: SearchModuleTranslationKey;
  t: ComponentTFunction;
  measurementSystemSymbol?: string;
};

type FormatNumberRangeParams = {
  range?: RangeFilter;
  language: Language;
};

type FormatPriceRangeParams = {
  range?: RangeFilter;
  language: Language;
  currency?: Currency;
};

type ResetPropertySubTypeSelectionParams = {
  state: SearchState;
  propertyTypeKey: keyof SearchFilterRequestPropertyTypeSubType;
  propertySubType: PropertySubType;
};

type ResetLocationParams = {
  state: SearchState;
  marketCountryCode: MarketCountryCode;
  placeDetails?: PlaceDetails[];
  placeIdToRemove?: string;
};

const createRangeChip = (field: RangeField, label?: string): FilterChip | undefined =>
  label
    ? {
        id: `${FILTER_CHIP_KINDS.range}:${field}`,
        isRemovable: true,
        kind: FILTER_CHIP_KINDS.range,
        field,
        label,
        variant: 'filter',
      }
    : undefined;

const getFormattedNumberRange = ({ range, language }: FormatNumberRangeParams): DisplayRange => ({
  min: range?.min ? formatNumber({ number: range.min, language }, { maximumFractionDigits: 0 }) : undefined,
  max: range?.max ? formatNumber({ number: range.max, language }, { maximumFractionDigits: 0 }) : undefined,
});

const getFormattedPriceRange = ({ range, language, currency }: FormatPriceRangeParams): DisplayRange => ({
  min: currency && range?.min ? formatPriceWithoutDecimals(range.min, language, currency) : undefined,
  max: currency && range?.max ? formatPriceWithoutDecimals(range.max, language, currency) : undefined,
});

export const getLocationChips = ({
  placeIds,
  placeName,
  placeDetails,
  marketCountryCode,
}: {
  placeIds?: string[];
  placeName?: string;
  placeDetails: PlaceDetails[];
  marketCountryCode: MarketCountryCode;
}): FilterChip[] => {
  if (!placeIds?.length) return [];

  const fallbackPlaceId = COUNTRYCODE_PLACEID_MAP[getNormalizedMarketCountryCode(marketCountryCode)];

  return placeIds.map((placeId) => {
    const label = placeDetails.find((detail) => detail.placeId === placeId)?.placeName ?? (placeIds.length === 1 ? (placeName ?? '') : '');

    return {
      id: `${FILTER_CHIP_KINDS.location}:${placeId}`,
      isRemovable: !(placeIds.length === 1 && placeId === fallbackPlaceId),
      kind: FILTER_CHIP_KINDS.location,
      label,
      value: placeId,
      variant: 'filter',
    };
  });
};

export const getMarketingTypeChip = (marketingType: PropertyMarketingType, t: ComponentTFunction): FilterChip => {
  return {
    id: `${FILTER_CHIP_KINDS.marketingType}:${marketingType}`,
    isRemovable: marketingType !== DEFAULT_PROPERTY_MARKETING_TYPE,
    kind: FILTER_CHIP_KINDS.marketingType,
    label: marketingType === PropertyMarketingType.sale ? t('search.searchbar.sale') : t('search.searchbar.rent'),
    value: marketingType,
    variant: 'filter',
  };
};

export const getRangeChips = ({
  filters,
  t,
  language,
  promptOptions,
}: {
  filters: RangesState;
  t: ComponentTFunction;
  language: Language;
  promptOptions: PromptOptions;
}): FilterChip[] => {
  const measurementSystemSymbol = promptOptions.measurementSystem ? UNIT_SYMBOL_MAP[promptOptions.measurementSystem] : undefined;
  const bedroomsRange = { min: filters.bedrooms?.min, max: filters.bedrooms?.max };
  const roomsRange = { min: filters.rooms?.min, max: filters.rooms?.max };

  return [
    createRangeChip(
      'rooms',
      convertRangeToText({
        range: roomsRange,
        translationKey: 'search.aiSearch.filters.rooms',
        t,
      }),
    ),
    createRangeChip(
      'bedrooms',
      convertRangeToText({
        range: bedroomsRange,
        translationKey: 'search.aiSearch.filters.bedrooms',
        t,
      }),
    ),
    createRangeChip(
      'bathrooms',
      filters.bathrooms?.min ? `${filters.bathrooms.min}+ ${t('search.aiSearch.filters.bathrooms')}` : undefined,
    ),
    createRangeChip(
      'constructionYear',
      convertRangeToText({
        range: filters.constructionYear ?? {},
        translationKey: 'search.aiSearch.filters.constructionYear',
        t,
      }),
    ),
    createRangeChip(
      'plotSurface',
      measurementSystemSymbol
        ? convertRangeToText({
            range: getFormattedNumberRange({ range: filters.plotSurface, language }),
            translationKey: 'search.aiSearch.filters.plotSurface',
            t,
            measurementSystemSymbol,
          })
        : undefined,
    ),
    createRangeChip(
      'totalSurface',
      measurementSystemSymbol
        ? convertRangeToText({
            range: getFormattedNumberRange({ range: filters.totalSurface, language }),
            translationKey: 'search.aiSearch.filters.totalSurface',
            t,
            measurementSystemSymbol,
          })
        : undefined,
    ),
    createRangeChip(
      'price',
      convertRangeToText({
        range: getFormattedPriceRange({ range: filters.price, language, currency: promptOptions.currency }),
        t,
      }),
    ),
  ].filter((chip): chip is FilterChip => Boolean(chip));
};

export const getFeatureChips = (features: SearchState['features'], t: ComponentTFunction): FilterChip[] =>
  getEntries(features).flatMap(([feature, isSelected]) => {
    if (!isSelected) return [];

    const translationKey = getFeatureKey(feature);
    if (!translationKey) return [];

    return [
      {
        id: `${FILTER_CHIP_KINDS.feature}:${feature}`,
        isRemovable: true,
        kind: FILTER_CHIP_KINDS.feature,
        label: t(translationKey),
        feature,
        variant: 'filter',
      } satisfies FilterChip,
    ];
  });

export const getPreferenceChips = (preferences: UIFacingPreferences | undefined, t: ComponentTFunction): FilterChip[] =>
  getSelectedPreferenceEntries(preferences, t).map(
    ({ preference, label }) =>
      ({
        id: `${FILTER_CHIP_KINDS.preference}:${preference}`,
        isRemovable: true,
        kind: FILTER_CHIP_KINDS.preference,
        label,
        preference,
        variant: 'preference',
      }) satisfies FilterChip,
  );

export const getPropertyTypeChips = ({
  propertyTypeState,
  objectTypeCategory,
  t,
}: {
  propertyTypeState: PropertyTypeState | undefined;
  objectTypeCategory: ObjectTypeCategory;
  t: ComponentTFunction;
}): FilterChip[] =>
  getPropertyTypeSelectionEntries(propertyTypeState, objectTypeCategory, t).map((selection) => {
    switch (selection.kind) {
      case PROPERTY_TYPE_SELECTION_KINDS.objectType:
        return {
          id: `${FILTER_CHIP_KINDS.objectType}:${selection.value}`,
          isRemovable: selection.value !== DEFAULT_BUSINESS_AREA,
          kind: FILTER_CHIP_KINDS.objectType,
          label: selection.label,
          value: selection.value,
          variant: 'filter',
        };
      case PROPERTY_TYPE_SELECTION_KINDS.propertyType:
        return {
          id: `${FILTER_CHIP_KINDS.propertyType}:${objectTypeCategory}:${selection.propertyType}`,
          isRemovable: true,
          kind: FILTER_CHIP_KINDS.propertyType,
          label: selection.label,
          propertyType: selection.propertyType,
          variant: 'filter',
        };
      case PROPERTY_TYPE_SELECTION_KINDS.propertySubType:
        return {
          id: `${FILTER_CHIP_KINDS.propertySubType}:${objectTypeCategory}:${selection.propertyType}:${selection.propertySubType}`,
          isRemovable: true,
          kind: FILTER_CHIP_KINDS.propertySubType,
          label: selection.label,
          propertyType: selection.propertyType,
          propertySubType: selection.propertySubType,
          variant: 'filter',
        };
    }
  });

const normalizePreferenceSorting = (state: SearchState) => {
  if (state.sortingOptionId === SortingOptionId.PREFERENCES && !isNonEmptyObject(state.preferences)) {
    state.sortingOptionId = DEFAULT_SORTING_OPTION_ID;
  }
};

const resetObjectTypeState = (state: SearchState) => {
  state.objectTypeCategory = DEFAULT_BUSINESS_AREA;
  state.propertyTypeState = structuredClone(initialPropertyTypeState);
};

const resetPropertyTypeSelection = (state: SearchState, propertyTypeKey: keyof SearchFilterRequestPropertyTypeSubType) => {
  if (state.objectTypeCategory !== BusinessArea.residential || propertyTypeKey === PropertyType.group) {
    resetObjectTypeState(state);
    return;
  }

  const propertyType = state.propertyTypeState[state.objectTypeCategory][propertyTypeKey];
  if (!propertyType) {
    return;
  }

  propertyType.selected = false;
  for (const subType of Object.values(propertyType.subTypes ?? {})) {
    subType.selected = false;
  }
};

const resetPropertySubTypeSelection = ({ state, propertyTypeKey, propertySubType }: ResetPropertySubTypeSelectionParams) => {
  const propertyType = state.propertyTypeState[state.objectTypeCategory][propertyTypeKey];
  const nextPropertySubType = propertyType?.subTypes?.[propertySubType];

  if (!nextPropertySubType) {
    return;
  }

  nextPropertySubType.selected = false;
  propertyType.selected = false;
};

const getNormalizedMarketCountryCode = (marketCountryCode: MarketCountryCode) =>
  marketCountryCode === MarketCountryCode['private-office'] ? MarketCountryCode.DE : marketCountryCode;

const getPlaceNameFromPlaceIds = (placeIds: string[], placeDetails: PlaceDetails[] = []) => {
  const placeNames = placeIds.map((placeId) => placeDetails.find((detail) => detail.placeId === placeId)?.placeName).filter(Boolean);

  return placeNames.length > 0 ? placeNames.join(', ') : undefined;
};

const resetLocation = ({ state, marketCountryCode, placeDetails = [], placeIdToRemove }: ResetLocationParams) => {
  const remainingPlaceIds = state.placeIds?.filter((placeId) => placeId !== placeIdToRemove) ?? [];
  const fallbackPlaceId = COUNTRYCODE_PLACEID_MAP[getNormalizedMarketCountryCode(marketCountryCode)];

  state.listingIds = undefined;
  state.searchRadius = 0;
  state.multiPolygon = undefined;

  if (remainingPlaceIds.length > 0) {
    state.placeIds = remainingPlaceIds;
    state.placeName = getPlaceNameFromPlaceIds(remainingPlaceIds, placeDetails);
    return;
  }

  state.placeIds = [fallbackPlaceId];
  state.placeName = getPlaceNameFromPlaceIds([fallbackPlaceId], placeDetails);
};

export const removeAiSearchFilterChip = ({
  state,
  chip,
  marketCountryCode,
  placeDetails,
}: {
  state: SearchState;
  chip: FilterChip;
  marketCountryCode: MarketCountryCode;
  placeDetails?: PlaceDetails[];
}): SearchState => {
  const nextState = structuredClone(state);

  if (!chip.isRemovable) {
    return nextState;
  }

  nextState.page = 1;

  switch (chip.kind) {
    case FILTER_CHIP_KINDS.marketingType:
      nextState.marketingType = DEFAULT_PROPERTY_MARKETING_TYPE;
      break;
    case FILTER_CHIP_KINDS.objectType:
      resetObjectTypeState(nextState);
      break;
    case FILTER_CHIP_KINDS.propertyType:
      resetPropertyTypeSelection(nextState, chip.propertyType);
      break;
    case FILTER_CHIP_KINDS.propertySubType:
      resetPropertySubTypeSelection({
        state: nextState,
        propertyTypeKey: chip.propertyType,
        propertySubType: chip.propertySubType,
      });
      break;
    case FILTER_CHIP_KINDS.location:
      resetLocation({
        state: nextState,
        marketCountryCode,
        placeDetails,
        placeIdToRemove: chip.value,
      });
      break;
    case FILTER_CHIP_KINDS.range:
      nextState[chip.field] = undefined;
      break;
    case FILTER_CHIP_KINDS.feature:
      nextState.features[chip.feature] = false;
      break;
    case FILTER_CHIP_KINDS.preference: {
      const preferences = { ...nextState.preferences };
      delete preferences[chip.preference];
      nextState.preferences = preferences;
      normalizePreferenceSorting(nextState);
      break;
    }
  }

  return nextState;
};

export const convertRangeToText = ({ range, translationKey, t, measurementSystemSymbol }: ConvertRangeToTextParams): string => {
  const measurementSuffix = measurementSystemSymbol ? ` ${measurementSystemSymbol}` : '';
  const labelSuffix = translationKey ? ` ${t(translationKey)}` : '';
  return formatDisplayRange({
    range,
    minimumLabel: t('search.aiSearch.preferences.min'),
    maximumLabel: t('search.aiSearch.preferences.max'),
    suffix: `${measurementSuffix}${labelSuffix}`,
    collapseEqualBounds: true,
  });
};
